import { useEffect, useRef, useCallback } from "react";

/* Single global observer for all scroll-reveal elements */
let globalObserver = null;
const observedElements = new Set();

const getObserver = () => {
  if (globalObserver) return globalObserver;
  globalObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("sr-visible");
          globalObserver.unobserve(entry.target);
          observedElements.delete(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: "0px 0px -30px 0px" }
  );
  return globalObserver;
};

/* Hook version — attaches a ref to the observer */
const useScrollReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = getObserver();
    observedElements.add(el);
    obs.observe(el);
    return () => { obs.unobserve(el); observedElements.delete(el); };
  }, []);
  return ref;
};

/* Component version — pure CSS driven, no state updates */
const ScrollReveal = ({ children, direction = "up", delay = 0, className = "" }) => {
  const ref = useCallback((node) => {
    if (!node) return;
    const obs = getObserver();
    observedElements.add(node);
    obs.observe(node);
  }, []);

  return (
    <div
      ref={ref}
      className={`sr sr-${direction} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

export { useScrollReveal, ScrollReveal };
export default ScrollReveal;
